// src/components/Header.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Box,
  Button,
  Badge,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import HomeIcon from "@mui/icons-material/Home";
import PersonIcon from "@mui/icons-material/Person";

const Header = ({ menuItems = [], logo = "/images/mihu-logo-trans.png", socialLinks, cartQty }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch (e) {
        console.error('Failed to parse user data', e);
        localStorage.removeItem('user');
      }
    }
  }, []);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
    window.location.href = '/';
  };

  // Smooth scroll handler for internal anchor links
  const handleAnchorClick = (e, link) => {
    if (link.startsWith("/#")) {
      e.preventDefault();
      const id = link.replace("/#", "");
      
      if (window.location.pathname === "/") {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        window.location.href = `/#${id}`;
      }
      handleMenuClose();
    } else if (link.startsWith("#")) {
      e.preventDefault();
      const id = link.replace("#", "");
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      handleMenuClose();
    }
  };

  const handleCartClick = () => {
      if (isAuthenticated) {
      let cart = [];
      try {
        const savedCart = localStorage.getItem("cart");
        console.log("saved cart: ", savedCart);

        if (savedCart) {
          cart = JSON.parse(savedCart);
          
          if (!Array.isArray(cart)) {
            console.warn("Cart is not an array, resetting");
            cart = [];
            localStorage.setItem("cart", JSON.stringify([]));
          }
        }
      } catch (e) {
        console.error("Failed to parse cart from localStorage", e);
        cart = [];
        localStorage.removeItem("cart");
      }

      localStorage.setItem("cart", JSON.stringify(cart));
      window.location.href = "/shopping-cart";
   } 
    else {
      // Save current page to return after login
      window.location.href = `/signin?redirect=${encodeURIComponent('/shopping-cart')}`;

    }
  };

  // Desktop navigation - add Hub and Profile for logged-in users
  const renderDesktopMenu = () => (
    <>
      {isAuthenticated && (
        <>
          <Button
            onClick={() => window.location.href = '/hub'}
            sx={{ color: "white" }}
            startIcon={<HomeIcon />}
          >
            HUB
          </Button>
          <Button
            onClick={() => window.location.href = '/profile'}
            sx={{ color: "white" }}
            startIcon={<PersonIcon />}
          >
            PROFILE
          </Button>
        </>
      )}
      {menuItems.map((item) => (
        <Button
          key={item.text}
          onClick={(e) => {
            if (item.link.startsWith("/#") || item.link.startsWith("#")) {
              handleAnchorClick(e, item.link);
            } else {
              window.location.href = item.link;
            }
          }}
          sx={{ color: "white" }}
        >
          {item.text}
        </Button>
      ))}
    </>
  );

  // Mobile navigation - add Hub and Profile for logged-in users
  const renderMobileMenu = () => (
    <>
      {isAuthenticated && (
        <>
          <MenuItem onClick={() => { window.location.href = '/hub'; handleMenuClose(); }}>
            <HomeIcon sx={{ mr: 1 }} /> HUB
          </MenuItem>
          <MenuItem onClick={() => { window.location.href = '/profile'; handleMenuClose(); }}>
            <PersonIcon sx={{ mr: 1 }} /> PROFILE
          </MenuItem>
          <Divider sx={{ my: 1, borderColor: "#333" }} />
        </>
      )}
      {menuItems.map((item) => (
        <MenuItem
          key={item.text}
          onClick={(e) => {
            if (item.link.startsWith("/#") || item.link.startsWith("#")) {
              handleAnchorClick(e, item.link);
            } else {
              window.location.href = item.link;
            }
            handleMenuClose();
          }}
        >
          {item.text}
        </MenuItem>
      ))}
    </>
  );

  const socials = socialLinks || (
    <Box
      component="ul"
      sx={{
        listStyle: "none",
        display: "flex",
        gap: "1rem",
        margin: 0,
        padding: 0,
        justifyContent: "center",
        alignItems: "center",
        flexWrap: "wrap",
      }}
    >
      <li>
        <a href="https://www.patreon.com/c/musicishealing" target="_blank" rel="noreferrer">
          <i className="fab fa-patreon fa-lg" style={{ color: "white" }} />
        </a>
      </li>
      <li>
        <a href="http://instagram.com/musicishealingus" target="_blank" rel="noreferrer">
          <i className="fab fa-instagram fa-lg" style={{ color: "white" }} />
        </a>
      </li>
      <li>
        <a href="https://www.youtube.com/channel/UCzFr7u6-hotJ32n1F9YG8zQ" target="_blank" rel="noreferrer">
          <i className="fab fa-youtube fa-lg" style={{ color: "white" }} />
        </a>
      </li>
      <li>
        <a href="https://www.twitch.tv/musicishealingus" target="_blank" rel="noreferrer">
          <i className="fab fa-twitch fa-lg" style={{ color: "white" }} />
        </a>
      </li>
      <li>
        <a href="https://www.mixcloud.com/musicishealingus" target="_blank" rel="noreferrer">
          <i className="fab fa-mixcloud fa-lg" style={{ color: "white" }} />
        </a>
      </li>
    </Box>
  );

  return (
    <>
      <AppBar position="fixed" sx={{ backgroundColor: "rgba(0,0,0,1.0)", width: "100%" }}>
        <Toolbar sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2 }}>
          {/* Logo */}
          <Box component="a" href="/" sx={{ display: "flex", alignItems: "center" }}>
            <img src={logo} alt="Music Is Healing Logo" style={{ height: "40px", objectFit: "contain" }} />
          </Box>

          {/* Desktop Menu */}
          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 2, alignItems: "center" }}>
            {renderDesktopMenu()}

            {/* Auth Buttons or User Menu */}
            {isAuthenticated ? (
              <>
                <Button 
                  color="inherit" 
                  startIcon={<AccountCircleIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  Hello, {user?.firstname}
                </Button>
                <Button 
                  color="inherit" 
                  onClick={handleLogout}
                  sx={{ color: 'lightcoral' }}
                >
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button 
                  color="inherit" 
                  onClick={() => window.location.href = '/signin'}
                  sx={{ color: 'lightblue' }}
                >
                  Sign In
                </Button>
                <Button 
                  variant="outlined"
                  href="https://www.patreon.com/c/musicishealing/membership"
                  target="_blank"
                  sx={{ 
                    color: 'white', 
                    borderColor: 'white',
                    '&:hover': {
                      borderColor: 'lightblue',
                      backgroundColor: 'rgba(173, 216, 230, 0.1)'
                    }
                  }}
                >
                  Become a Member
                </Button>
              </>
            )}

                      <a
            href="tel:617-397-4440"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: "white",
              color: "red",
              borderRadius: "25px",
              padding: "6px 12px",
              border: "1px solid red",
              fontWeight: "bold",
              fontSize: "0.85rem",
              textDecoration: "none",
            }}
          >
            <img
              src="/images/hotline-icon.png"
              alt="Hotline"
              style={{ width: "18px", marginRight: "6px", verticalAlign: "middle" }}
            />
            HOTLINE
          </a>
                      {/* Cart Icon */}

            <IconButton 
              color="inherit" 
              onClick={handleCartClick} 
              sx={{ 
                color: "dodgerblue", 
                position: "relative",
                ml: 1
              }}
            >
              <Badge
                badgeContent={cartQty}
                color="error"
                showZero
                overlap="circular"
                sx={{
                  "& .MuiBadge-badge": { 
                    right: 4, 
                    top: 4, 
                    fontSize: "0.75rem", 
                    height: 18, 
                    minWidth: 18, 
                    borderRadius: "50%" 
                  },
                }}
              >
                <ShoppingCartIcon fontSize="medium" />
              </Badge>
            </IconButton>

            {socials}
          </Box>

          {/* Mobile Menu */}
          <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1 }}>
            {/* Cart Icon always visible */}
                                  <a
            href="tel:617-397-4440"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: "white",
              color: "red",
              borderRadius: "25px",
              padding: "6px 12px",
              border: "1px solid red",
              fontWeight: "bold",
              fontSize: "0.85rem",
              textDecoration: "none",
            }}
          >
            <img
              src="/images/hotline-icon.png"
              alt="Hotline"
              style={{ width: "18px", marginRight: "6px", verticalAlign: "middle" }}
            />
            HOTLINE
          </a>
            <IconButton onClick={handleCartClick} sx={{ color: "dodgerblue" }}>
              <Badge
                badgeContent={cartQty}
                color="error"
                showZero
                overlap="circular"
                sx={{
                  "& .MuiBadge-badge": { 
                    right: 4, 
                    top: 4, 
                    fontSize: "0.75rem", 
                    height: 18, 
                    minWidth: 18, 
                    borderRadius: "50%" 
                  },
                }}
              >
                <ShoppingCartIcon fontSize="medium" />
              </Badge>
            </IconButton>

            {/* Hamburger menu */}
            <IconButton edge="end" color="inherit" onClick={handleMenuOpen}>
              <MenuIcon />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              keepMounted
              PaperProps={{
                sx: { width: "240px", maxWidth: "80vw", backgroundColor: "#111", color: "white" },
              }}
            >
              {renderMobileMenu()}
              
              {/* Auth options in mobile menu */}
              {isAuthenticated ? (
                <>
                  <Divider sx={{ my: 1, borderColor: "#333" }} />
                  <MenuItem disabled sx={{ opacity: 0.7 }}>
                    <AccountCircleIcon sx={{ mr: 1 }} />
                    {user?.firstname}
                  </MenuItem>
                  <MenuItem onClick={handleLogout} sx={{ color: 'lightcoral' }}>
                    Logout
                  </MenuItem>
                </>
              ) : (
                <>
                  <Divider sx={{ my: 1, borderColor: "#333" }} />
                  <MenuItem onClick={() => window.location.href = '/signin'}>
                    Sign In
                  </MenuItem>
                  <MenuItem onClick={() => window.location.href = '/register'}>
                    Register
                  </MenuItem>
                </>
              )}
              
              <Divider sx={{ my: 1, borderColor: "#333" }} />
              <Box sx={{ px: 2, pb: 1 }}>{socials}</Box>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Push content below AppBar */}
      <Toolbar />
    </>
  );
};

export default Header;

// import React, { useState } from "react";
// import AppBar from "@mui/material/AppBar";
// import Toolbar from "@mui/material/Toolbar";
// import IconButton from "@mui/material/IconButton";
// import MenuIcon from "@mui/icons-material/Menu";
// import Button from "@mui/material/Button";
// import Drawer from "@mui/material/Drawer";
// import List from "@mui/material/List";
// import ListItem from "@mui/material/ListItem";
// import ListItemText from "@mui/material/ListItemText";
// import Box from "@mui/material/Box";
// import Collapse from "@mui/material/Collapse";
// import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
// import ExpandLessIcon from "@mui/icons-material/ExpandLess";

// export default function Header({ menuItems = [], logo = "/images/mihu-logo-trans.png" }) {
//   const [drawerOpen, setDrawerOpen] = useState(false);
//   const [openSubmenus, setOpenSubmenus] = useState({});

//   const toggleDrawer = () => setDrawerOpen(!drawerOpen);

//   const toggleSubmenu = (index) => {
//     setOpenSubmenus((prev) => ({ ...prev, [index]: !prev[index] }));
//   };

//   // Drawer content for mobile
//   const drawer = (
//     <Box
//       sx={{
//         width: 250,
//         maxWidth: "80vw",
//         bgcolor: "black",
//         height: "100%",
//         color: "white",
//         p: 2,
//       }}
//       role="presentation"
//       onClick={toggleDrawer}
//       onKeyDown={toggleDrawer}
//     >
//       <List>
//         {menuItems.map((item, index) => (
//           <React.Fragment key={index}>
//             <ListItem button component="a" href={item.link} onClick={() => item.submenu && toggleSubmenu(index)}>
//               <ListItemText primary={item.text} />
//               {item.submenu &&
//                 (openSubmenus[index] ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
//             </ListItem>

//             {item.submenu && (
//               <Collapse in={openSubmenus[index]} timeout="auto" unmountOnExit>
//                 <List component="div" disablePadding>
//                   {item.submenu.map((sub, i) => (
//                     <ListItem
//                       key={i}
//                       button
//                       component="a"
//                       href={sub.link}
//                       sx={{ pl: 4 }}
//                     >
//                       <ListItemText primary={sub.text} />
//                     </ListItem>
//                   ))}
//                 </List>
//               </Collapse>
//             )}
//           </React.Fragment>
//         ))}
//       </List>
//     </Box>
//   );

//   return (
//     <>
//       <AppBar position="fixed" sx={{ bgcolor: "black", width: "100%" }}>
//         <Toolbar sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
//           {/* Logo */}
//           <Box component="a" href="/" sx={{ display: "flex", alignItems: "center" }}>
//             <img src={logo} alt="Logo" style={{ height: "50px", width: "auto" }} />
//           </Box>

//           {/* Desktop menu */}
//           <Box sx={{ display: { xs: "none", md: "flex" }, gap: 2, flexGrow: 1, justifyContent: "flex-end" }}>
//             {menuItems.map((item, i) => (
//               <Button
//                 key={i}
//                 href={item.link}
//                 sx={{ color: "white", fontWeight: "bold" }}
//               >
//                 {item.text}
//               </Button>
//             ))}
//           </Box>

//           {/* Hamburger menu for mobile */}
//           <IconButton
//             edge="end"
//             color="inherit"
//             aria-label="menu"
//             onClick={toggleDrawer}
//             sx={{ display: { xs: "flex", md: "none" } }}
//           >
//             <MenuIcon />
//           </IconButton>
//         </Toolbar>
//       </AppBar>

//       {/* Drawer for mobile */}
//       <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer} PaperProps={{ sx: { width: 250, maxWidth: "80vw" } }}>
//         {drawer}
//       </Drawer>

//       {/* Push content down so AppBar doesn't cover page content */}
//       <Toolbar />
//     </>
//   );
// }
